import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'
import classnames from 'classnames'

import { PictureCropped } from 'components/Picture'
import { Button, IonIcon, Modal, Label } from 'controls'

import { MAX_IMAGES_PER_PAGE } from './config'
import { getGridClassname, getPictureImageSize } from './utils'

import { seoGallery, gridGalleryStyles } from './styles'

const Gallery = dynamic(() => import('./Gallery'), { ssr: false })
const GalleryActions = dynamic(() => import('./GalleryActions'), { ssr: false })

const GridGallery = ({
  imagesJson,
  className,
  imagesAlt,
  handleSelectImage
}) => {
  const newJsonImages = imagesJson.slice(0, MAX_IMAGES_PER_PAGE)
  const totalImages = newJsonImages.length

  return (
    <div
      className={classnames(
        className,
        'vip-grid-gallery',
        getGridClassname(totalImages)
      )}
    >
      {newJsonImages.map((image, index) => (
        <PictureCropped
          key={index}
          image={image.url}
          format="webp"
          className="vip-grid-gallery-image"
          onClick={() => handleSelectImage(index)}
          alt={`${imagesAlt} | Foto ${index + 1} de ${totalImages}`}
          {...getPictureImageSize(index)}
        />
      ))}

      {totalImages > 1 && totalImages % 2 === 0 && (
        <PictureCropped
          format="webp"
          image={newJsonImages[0].url}
          className="vip-grid-gallery-image blured-image"
          onClick={() => handleSelectImage(0)}
          {...getPictureImageSize(totalImages)}
        />
      )}

      <style jsx>{gridGalleryStyles}</style>
    </div>
  )
}

const SeoGallery = ({ className, imagesJson, imagesAlt, isExternal, externalLabel, ...restProps }) => {
  const [galleryWasMounted, setGalleryWasMounted] = React.useState(false)
  const [hideGallery, setHideGallery] = React.useState(true)
  const [selectedIndexImage, setSelectedIndexImage] = React.useState(0)
  const [isFullscreenAvailable, setIsFullscreenAvailable] = React.useState(
    false
  )
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const ref = React.useRef(null)

  const externalLabelPosition = (length) => {
    if (length == 1) return '5%'
    else if (length > 1 && length <= 3) return '35%'
    else return '47%'
  }

  useEffect(() => {
    document.onfullscreenchange = function(event) {
      if (!document.fullscreenElement) {
        setHideGallery(true)
      }
    }

    return () => {
      document.onfullscreenchange = function() {}
    }
  }, [])

  useEffect(() => {
    document.onwebkitfullscreenchange = function(event) {
      if (!document.webkitFullscreenElement) {
        setHideGallery(true)
      }
    }

    return () => {
      document.onwebkitfullscreenchange = function() {}
    }
  }, [])

  useEffect(() => {
    document.onmsfullscreenchange = function(event) {
      if (!document.msFullscreenElement) {
        setHideGallery(true)
      }
    }

    return () => {
      document.onmsfullscreenchange = function() {}
    }
  }, [])

  useEffect(() => {
    setIsFullscreenAvailable(document.fullscreenEnabled)
  }, [])

  const handleFullScreen = () => {
    const galleryWrapper = document.getElementById('imageGallery')

    if (galleryWrapper) {
      const elem = galleryWrapper.firstElementChild

      if (elem.requestFullscreen) {
        elem.requestFullscreen()
        setHideGallery(false)
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen()
        setHideGallery(false)
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen()
        setHideGallery(false)
      } else {
        setIsModalOpen(true)
      }
    }
  }

  const handleSelectImage = index => {
    if (!galleryWasMounted) {
      setGalleryWasMounted(true)
    }

    setSelectedIndexImage(index)
    handleFullScreen()
  }

  return (
    <div className="vip-gallery-container">
      <div className={classnames({ 'hide-vip-gallery': hideGallery })}>
        {galleryWasMounted && (
          <Gallery
            {...restProps}
            imagesAlt={imagesAlt}
            imagesJson={imagesJson}
            disableKeyDown={true}
            setGalleryRef={ref}
            startIndex={selectedIndexImage}
          />
        )}
      </div>
      {isExternal && 
        <Label
          color="tertiary"
          size="normal"
          styleType="normal"
          style={{
            position: 'absolute',
            top: 'initial',
            right: externalLabelPosition(imagesJson.length) ,
            opacity: '60%',
            bottom: '1.5rem',
            zIndex: '99'
          }}
        >
          {externalLabel}
        </Label>
      }
      <GalleryActions
        lat={restProps.lat}
        lng={restProps.lng}
        images={imagesJson}
        className="gallery-actions"
      />

      <GridGallery
        imagesAlt={imagesAlt}
        className={className}
        imagesJson={imagesJson}
        handleSelectImage={handleSelectImage}
      />

      <Modal
        cover
        coverOnMobile
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        position='center'
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center'
        }}
        overlayStyle={{backgroundColor: 'white'}}
      >
        <Gallery
          {...restProps}
          imagesAlt={imagesAlt}
          imagesJson={imagesJson}
          disableKeyDown={true}
          setGalleryRef={ref}
          startIndex={selectedIndexImage}
        />
      </Modal>

      <Button
        color="primary"
        onClick={() => handleSelectImage(0)}
        className="vip-gallery-show-full-screen-button"
      >
        <IonIcon icon="md-image" /> Ver galería
      </Button>

      <style jsx>{seoGallery}</style>
    </div>
  )
}

export default SeoGallery
